import { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import type { Locale, TranslationKeys, I18nContextValue } from './types';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, LOCALE_STORAGE_KEY } from './types';
import { ru } from './ru';

// Translation dictionaries keyed by locale.
// Russian is loaded synchronously (default + fallback). Other locales loaded on demand.
const translations: Partial<Record<Locale, TranslationKeys>> = {
  ru,
};

// Locale loaders for on-demand loading
const localeLoaders: Record<Locale, () => Promise<TranslationKeys>> = {
  ru: () => Promise.resolve(ru),
  en: () => import('./en').then((m) => m.en),
};

/** Register a translation dictionary for a locale */
export function registerTranslations(locale: Locale, dict: TranslationKeys): void {
  translations[locale] = dict;
}

/** Load a locale dictionary if not already loaded */
async function ensureLocaleLoaded(locale: Locale): Promise<TranslationKeys> {
  if (translations[locale]) return translations[locale]!;
  const loader = localeLoaders[locale];
  if (!loader) return ru; // fallback
  const dict = await loader();
  translations[locale] = dict;
  return dict;
}

/** Detect the user's preferred locale */
export function detectLocale(): Locale {
  // 1. Check localStorage
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) {
      return stored as Locale;
    }
  } catch {
    // localStorage unavailable (e.g. incognito in some browsers)
  }

  // 2. Check navigator.language
  if (typeof navigator !== 'undefined' && navigator.language) {
    const browserLang = navigator.language.split('-')[0].toLowerCase();
    if (SUPPORTED_LOCALES.includes(browserLang as Locale)) {
      return browserLang as Locale;
    }
  }

  // 3. Default
  return DEFAULT_LOCALE;
}

/** Russian pluralization helper: picks the correct form based on count */
export function pluralize(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 19) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? detectLocale());
  const [, setLoadTick] = useState(0);

  // Eagerly load the active locale's dictionary
  useEffect(() => {
    if (!translations[locale]) {
      ensureLocaleLoaded(locale).then(() => {
        setLoadTick((n) => n + 1); // trigger re-render with loaded dict
      });
    }
  }, [locale]);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const t = useCallback(
    (key: keyof TranslationKeys): string => {
      const dict = translations[locale];
      if (dict) {
        const value = dict[key];
        if (value !== undefined) return value;
      }
      // Fallback to Russian (always loaded)
      const fallback = ru[key];
      return fallback !== undefined ? fallback : key;
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, t, setLocale }),
    [locale, t, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Fallback t() for use outside I18nProvider (e.g. tests without provider) */
function fallbackT(key: keyof TranslationKeys): string {
  const value = ru[key];
  return value !== undefined ? value : key;
}

const fallbackContext: I18nContextValue = {
  locale: DEFAULT_LOCALE,
  t: fallbackT,
  setLocale: () => {},
};

/**
 * Hook to access i18n context.
 * If no I18nProvider is found (e.g. in tests), returns a fallback
 * context using the default locale (Russian).
 */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  return ctx ?? fallbackContext;
}
